import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getRuntimeConfig } from '../config/runtimeConfig';
import ActionPromptModal from '../components/ActionPromptModal';

const LIVE_CREW_STATUSES = new Set(['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending']);
const TERMINAL_CREW_STATUSES = new Set(['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed']);
const CREW_RECENT_VERDICTS_WINDOW_MS = 72 * 60 * 60 * 1000;

function getCrewOutcomeSummary(trial, crewVotes, currentAppUserId) {
  const latestVotes = new Map();
  for (const vote of crewVotes || []) {
    const key = `${vote.trial_id}:${vote.user_id}`;
    const nextTs = new Date(vote.created_at || 0).getTime();
    const prev = latestVotes.get(key);
    const prevTs = prev ? new Date(prev.created_at || 0).getTime() : -Infinity;
    if (!prev || nextTs >= prevTs) latestVotes.set(key, vote);
  }

  let votesA = 0;
  let votesB = 0;
  let userChoice = null;
  for (const vote of latestVotes.values()) {
    if (vote.vote_side === 'A') votesA += 1;
    if (vote.vote_side === 'B') votesB += 1;
    if (vote.user_id === currentAppUserId) userChoice = vote.vote_side;
  }

  const totalCrewVotes = votesA + votesB;
  const crewLeader = votesA === votesB ? null : (votesA > votesB ? 'A' : 'B');
  const crewLeaderLabel = crewLeader === 'A' ? trial.side_a : crewLeader === 'B' ? trial.side_b : null;
  const runnerUpLabel = crewLeader === 'A' ? trial.side_b : crewLeader === 'B' ? trial.side_a : null;
  const margin = Math.abs(votesA - votesB);
  const crewSplitLabel = totalCrewVotes === 0
    ? 'No crew votes yet'
    : crewLeader
      ? `${crewLeaderLabel} ${votesA > votesB ? votesA : votesB}-${votesA > votesB ? votesB : votesA}`
      : `Crew split ${votesA}-${votesB}`;
  const yourVoteCanSwing = !userChoice && totalCrewVotes > 0 && margin <= 1;

  const winnerSide = Number(trial.votes_a || 0) === Number(trial.votes_b || 0)
    ? null
    : Number(trial.votes_a || 0) > Number(trial.votes_b || 0) ? 'A' : 'B';
  const crewWon = winnerSide && crewLeader ? winnerSide === crewLeader : null;

  return {
    totalCrewVotes,
    votesA,
    votesB,
    crewLeader,
    crewLeaderLabel,
    runnerUpLabel,
    crewSplitLabel,
    yourVoteCanSwing,
    userChoice,
    crewWon,
    winnerSide,
  };
}

function LeaderboardList({ title, rows, emptyLabel = 'No activity yet.' }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="text-sm font-semibold text-slate-100 mb-3">{title}</div>
      <div className="space-y-2">
        {!rows?.length ? (
          <div className="text-xs text-slate-400">{emptyLabel}</div>
        ) : (
          rows.slice(0, 10).map((row, idx) => (
            <div key={`${row.user_id}_${idx}`} className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2">
              <div className="text-sm text-slate-200 truncate pr-3">{idx + 1}. {row.username || 'Unknown'}</div>
              <div className="text-sm font-bold text-slate-100">{row.score}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-xl font-black text-slate-100 mt-1">{value}</div>
      {sub ? <div className="text-[11px] text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function CrewTrialCard({ trial, contextLabel }) {
  const status = String(trial.status || '').toLowerCase();
  const statusLabel = status === 'sudden_death_open'
    ? 'Overtime live'
    : status === 'closed_pending_verdict'
      ? 'Verdict pending'
      : status === 'sudden_death_closed_pending'
        ? 'Overtime verdict pending'
        : LIVE_CREW_STATUSES.has(status)
          ? 'Live now'
          : 'Verdict landed';
  const crewOutcome = trial.crewOutcome || null;
  const isTerminal = TERMINAL_CREW_STATUSES.has(status);

  let crewLine = null;
  if (crewOutcome?.totalCrewVotes > 0 && !isTerminal) {
    if (crewOutcome.yourVoteCanSwing) {
      crewLine = `Your vote can still swing the crew toward ${crewOutcome.crewLeaderLabel || 'either side'}.`;
    } else if (crewOutcome.crewLeaderLabel) {
      crewLine = `Your crew is backing ${crewOutcome.crewLeaderLabel}.`;
    } else {
      crewLine = 'Your crew is split right now.';
    }
  } else if (crewOutcome && isTerminal) {
    if (crewOutcome.totalCrewVotes === 0) {
      crewLine = 'The verdict landed before your crew really picked a side.';
    } else if (crewOutcome.crewWon === true) {
      crewLine = `Your crew backed ${crewOutcome.crewLeaderLabel} and won this one.`;
    } else if (crewOutcome.crewWon === false && crewOutcome.crewLeaderLabel && crewOutcome.runnerUpLabel) {
      crewLine = `Your crew backed ${crewOutcome.crewLeaderLabel} and lost to ${crewOutcome.runnerUpLabel}.`;
    } else {
      crewLine = 'Your crew finished split on this one.';
    }
  }

  return (
    <Link
      to={`/t/${trial.id}`}
      className="block rounded-xl border border-slate-700 bg-slate-900/45 p-3 transition hover:border-cyan-500/30 hover:bg-slate-900/70"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {contextLabel ? (
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-200/75">
              {contextLabel}
            </div>
          ) : null}
          <div className="mt-1 text-base font-bold text-slate-100 line-clamp-2">{trial.title}</div>
          <div className="mt-1 text-sm text-slate-300 line-clamp-1">
            {trial.side_a} vs {trial.side_b}
          </div>
          {crewLine ? (
            <div className="mt-2 text-xs font-medium text-cyan-100/90 line-clamp-2">
              {crewLine}
            </div>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {statusLabel}
          </div>
          <div className="mt-1 text-xs text-slate-500">{Number(trial.total_votes || 0)} votes</div>
          {crewOutcome ? (
            <div className="mt-2 text-[11px] font-semibold text-slate-300">
              {crewOutcome.crewSplitLabel}
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export default function CrewPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [debugHint, setDebugHint] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [refreshingStats, setRefreshingStats] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [refreshPromptOpen, setRefreshPromptOpen] = useState(false);
  const [refreshPromptDays, setRefreshPromptDays] = useState('14');
  const [currentAppUserId, setCurrentAppUserId] = useState(null);
  const [liveActivityLoading, setLiveActivityLoading] = useState(true);
  const [liveActivityError, setLiveActivityError] = useState('');
  const [crewTrials, setCrewTrials] = useState([]);

  useEffect(() => {
    let alive = true;
    let authSub = null;

    const loadStats = async (attempt = 0) => {
      setLoading(true);
      setError('');
      if (attempt === 0) setDebugHint('');
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        const session = sessionRes?.session || null;
        if (!session?.user?.id) {
          if (!alive) return;
          setDebugHint('Waiting for sign-in…');
          setLoading(false);
          return;
        }

        const { data: appUserRow, error: appUserErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();
        if (appUserErr) throw appUserErr;
        if (!alive) return;
        setCurrentAppUserId(appUserRow?.id || null);

        const { data, error: rpcErr } = await supabase.rpc('get_crew_stats', { p_crew_id: id });
        if (rpcErr) throw rpcErr;
        if (!alive) return;
        setStats(data || null);
        if ((data?.error === 'join_required' || !data) && attempt < 2) {
          setDebugHint('No crew access yet (will retry)…');
          setTimeout(() => {
            if (alive) loadStats(attempt + 1);
          }, 500);
          return;
        }
      } catch (e) {
        console.error('[crew-page] get_crew_stats_failed', e);
        if (alive) setError(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    const loadAdminStatus = async () => {
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        const authUserId = sessionRes?.session?.user?.id || null;
        if (!authUserId) {
          if (alive) setIsAdmin(false);
          return;
        }
        const cfg = await getRuntimeConfig();
        const runtimeAdmins = new Set((cfg?.adminAuthUserIds || []).map((v) => String(v)));
        if (runtimeAdmins.has(String(authUserId))) {
          if (alive) setIsAdmin(true);
          return;
        }
        const { data: adminRow } = await supabase
          .from('moderation_admins')
          .select('auth_user_id')
          .eq('auth_user_id', authUserId)
          .eq('is_active', true)
          .maybeSingle();
        if (alive) setIsAdmin(Boolean(adminRow?.auth_user_id));
      } catch {
        if (alive) setIsAdmin(false);
      }
    };

    loadStats(0);
    loadAdminStatus();
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (!alive) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        loadStats(0);
        loadAdminStatus();
      }
    });
    authSub = listener?.subscription || null;

    return () => {
      alive = false;
      try { authSub?.unsubscribe?.(); } catch {}
    };
  }, [id]);

  const isJoinRequired = stats && stats.ok === false && stats.error === 'join_required';
  const top7d = useMemo(() => (Array.isArray(stats?.top_members_7d) ? stats.top_members_7d : []), [stats]);
  const topAll = useMemo(() => (Array.isArray(stats?.top_members_all_time) ? stats.top_members_all_time : []), [stats]);

  useEffect(() => {
    let alive = true;

    const loadCrewTrials = async () => {
      if (!id) return;
      setLiveActivityLoading(true);
      setLiveActivityError('');
      try {
        const { data: memberRows, error: memberErr } = await supabase
          .from('crew_members')
          .select('user_id')
          .eq('crew_id', id);
        if (memberErr) throw memberErr;

        const memberIds = [...new Set((memberRows || []).map((row) => row?.user_id).filter(Boolean))];
        if (memberIds.length === 0) {
          if (!alive) return;
          setCrewTrials([]);
          return;
        }

        const memberIdCsv = memberIds.join(',');

        const { data: trialsData, error: trialsErr } = await supabase
          .from('trials')
          .select('id,title,side_a,side_b,status,votes_a,votes_b,total_votes,created_at,closed_at,created_by,creator_id')
          .or(`created_by.in.(${memberIdCsv}),creator_id.in.(${memberIdCsv})`)
          .order('created_at', { ascending: false })
          .limit(40);
        if (trialsErr) throw trialsErr;

        const trialIds = (trialsData || []).map((row) => row.id).filter(Boolean);
        let crewVoteRows = [];
        if (trialIds.length > 0) {
          const { data: voteRows, error: crewVotesErr } = await supabase
            .from('votes')
            .select('trial_id,user_id,vote_side,created_at')
            .in('trial_id', trialIds)
            .in('user_id', memberIds);
          if (crewVotesErr) throw crewVotesErr;
          crewVoteRows = voteRows || [];
        }

        const votesByTrialId = new Map();
        for (const vote of crewVoteRows) {
          const key = vote?.trial_id;
          if (!key) continue;
          const next = votesByTrialId.get(key) || [];
          next.push(vote);
          votesByTrialId.set(key, next);
        }

        let votedIds = new Set();
        if (currentAppUserId && trialIds.length > 0) {
          const { data: voteRows, error: voteErr } = await supabase
            .from('votes')
            .select('trial_id')
            .eq('user_id', currentAppUserId)
            .in('trial_id', trialIds);
          if (voteErr) throw voteErr;
          votedIds = new Set((voteRows || []).map((row) => row?.trial_id).filter(Boolean));
        }

        if (!alive) return;
        setCrewTrials((trialsData || []).map((row) => ({
          ...row,
          hasVoted: votedIds.has(row.id),
          creatorCanonicalId: row.created_by || row.creator_id || null,
          crewOutcome: getCrewOutcomeSummary(row, votesByTrialId.get(row.id) || [], currentAppUserId),
        })));
      } catch (e) {
        console.error('[crew-page] live_activity_failed', e);
        if (alive) {
          setLiveActivityError(e?.message || String(e));
          setCrewTrials([]);
        }
      } finally {
        if (alive) setLiveActivityLoading(false);
      }
    };

    loadCrewTrials();
    return () => {
      alive = false;
    };
  }, [id, currentAppUserId]);

  const liveCrewTrials = useMemo(
    () => crewTrials.filter((trial) => LIVE_CREW_STATUSES.has(String(trial.status || '').toLowerCase())).slice(0, 4),
    [crewTrials],
  );
  const waitingOnYourVote = useMemo(
    () => crewTrials
      .filter((trial) => LIVE_CREW_STATUSES.has(String(trial.status || '').toLowerCase()))
      .filter((trial) => !trial.hasVoted && trial.creatorCanonicalId !== currentAppUserId)
      .slice(0, 4),
    [crewTrials, currentAppUserId],
  );
  const landedForCrew = useMemo(
    () => crewTrials
      .filter((trial) => TERMINAL_CREW_STATUSES.has(String(trial.status || '').toLowerCase()))
      .filter((trial) => {
        const landedAt = new Date(trial.closed_at || trial.created_at || 0).getTime();
        return Number.isFinite(landedAt) && (Date.now() - landedAt) <= CREW_RECENT_VERDICTS_WINDOW_MS;
      })
      .sort((a, b) => {
        const aTs = new Date(a.closed_at || a.created_at || 0).getTime();
        const bTs = new Date(b.closed_at || b.created_at || 0).getTime();
        return bTs - aTs;
      })
      .slice(0, 4),
    [crewTrials],
  );

  const refreshCrewStats = async () => {
    if (!id || !isAdmin || refreshingStats) return;
    setRefreshingStats(true);
    setActionMessage('');
    try {
      const days = Math.max(1, Math.min(365, Number.parseInt(String(refreshPromptDays), 10) || 14));
      const { data, error: rpcErr } = await supabase.rpc('admin_recompute_crew_stats', {
        p_crew_id: id,
        p_days: days,
      });
      if (rpcErr) throw rpcErr;
      if (!data?.ok) throw new Error(data?.error || 'refresh_failed');
      const { data: statsData, error: statsErr } = await supabase.rpc('get_crew_stats', { p_crew_id: id });
      if (statsErr) throw statsErr;
      setStats(statsData || null);
      setActionMessage(`Stats refreshed (${data?.days_recomputed || days} days).`);
    } catch (e) {
      console.error('[crew-page] admin_recompute_crew_stats_failed', e);
      setActionMessage(`Refresh failed: ${e?.message || String(e)}`);
    } finally {
      setRefreshingStats(false);
    }
  };

  const openRefreshPrompt = () => {
    setRefreshPromptDays('14');
    setRefreshPromptOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-50">Crew Arena</h1>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">See what side your crew is backing, where your vote can still swing it, and whether your people won or lost the call.</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={openRefreshPrompt}
                disabled={refreshingStats}
                className="rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-60 border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200"
              >
                {refreshingStats ? 'Refreshing…' : 'Refresh (admin)'}
              </button>
            )}
            <Link to="/" className="rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-100">
              Back
            </Link>
          </div>
        </div>
        {actionMessage ? (
          <div className={`rounded-lg border px-3 py-2 text-xs ${actionMessage.startsWith('Refresh failed') ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100'}`}>
            {actionMessage}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 text-sm text-slate-300">Loading crew stats…</div>
        ) : error ? (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>
        ) : isJoinRequired ? (
            <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
              <div className="text-lg font-bold text-slate-100">This arena is for members only</div>
              <div className="text-sm text-slate-300 mt-1">Join the crew to see the standings, the streak, and who is actually carrying the arguments.</div>
            </div>
        ) : !stats?.ok ? (
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 text-sm text-slate-300">Could not load crew stats.</div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900 p-4">
              <div className="text-lg font-bold text-slate-100">{stats.crew_emoji || '👥'} {stats.crew_name}</div>
              <div className="mt-3 text-sm text-slate-300">
                Your people are already in the room. Start with what is live, what is waiting on you, and what just landed.
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1 font-semibold text-cyan-200">
                  🔥 {stats.current_streak || 0}-day streak
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-600/70 bg-slate-900/60 px-3 py-1 font-semibold text-slate-200">
                  {stats.active_members_7d || 0} voices showed up this week
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Your Crew Is In This</div>
                <div className="rounded-xl border border-slate-700 bg-slate-800/45 p-3 space-y-2">
                  {liveActivityLoading ? (
                    <div className="text-sm text-slate-400">Loading active crew arguments…</div>
                  ) : liveActivityError ? (
                    <div className="text-sm text-slate-400">Couldn&apos;t load live crew activity right now.</div>
                  ) : liveCrewTrials.length === 0 ? (
                    <div className="text-sm text-slate-400">The room is between arguments right now. Be ready when the next one sparks.</div>
                  ) : (
                    liveCrewTrials.map((trial) => (
                      <CrewTrialCard key={trial.id} trial={trial} contextLabel="Your side is already in play" />
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Your Vote Still Moves It</div>
                <div className="rounded-xl border border-slate-700 bg-slate-800/45 p-3 space-y-2">
                  {liveActivityLoading ? (
                    <div className="text-sm text-slate-400">Checking where your vote is still missing…</div>
                  ) : liveActivityError ? (
                    <div className="text-sm text-slate-400">Couldn&apos;t load what your crew is still waiting on.</div>
                  ) : waitingOnYourVote.length === 0 ? (
                    <div className="text-sm text-slate-400">You are caught up for now. The next crew split will pull you back in.</div>
                  ) : (
                    waitingOnYourVote.map((trial) => (
                      <CrewTrialCard key={trial.id} trial={trial} contextLabel="Your vote can still move your crew" />
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Your Crew Just Won Or Lost</div>
                <div className="rounded-xl border border-slate-700 bg-slate-800/45 p-3 space-y-2">
                  {liveActivityLoading ? (
                    <div className="text-sm text-slate-400">Loading fresh crew verdicts…</div>
                  ) : liveActivityError ? (
                    <div className="text-sm text-slate-400">Couldn&apos;t load fresh crew verdicts right now.</div>
                  ) : landedForCrew.length === 0 ? (
                    <div className="text-sm text-slate-400">No recent crew verdicts have landed yet.</div>
                  ) : (
                    landedForCrew.map((trial) => (
                      <CrewTrialCard key={trial.id} trial={trial} />
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">This Week</div>
              <div className="grid grid-cols-3 gap-3">
                <MetricCard label="Takes cast" value={stats.votes_7d || 0} />
                <MetricCard label="Debates live" value={stats.trials_7d || 0} />
                <MetricCard label="Voices in" value={stats.active_members_7d || 0} />
              </div>
              <LeaderboardList title="Who Carried the Room (7d)" rows={top7d} emptyLabel="No one has really stirred it up yet." />
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/35 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">All Time</div>
              <div className="grid grid-cols-3 gap-3">
                <MetricCard label="Takes cast" value={stats.votes_all_time || 0} />
                <MetricCard label="Debates live" value={stats.trials_all_time || 0} />
                <MetricCard label="Voices in" value={stats.active_members_all_time || 0} />
              </div>
              <div className="mt-3">
                <LeaderboardList title="All-Time Standings" rows={topAll} emptyLabel="No all-time leaders yet." />
              </div>
            </div>
          </>
        )}
        {debugHint && !loading && !error ? (
          <div className="text-[11px] text-slate-500">{debugHint}</div>
        ) : null}
      </div>
      <ActionPromptModal
        open={refreshPromptOpen}
        title="Refresh crew stats"
        description="Recompute how many days?"
        inputType="number"
        value={refreshPromptDays}
        placeholder="14"
        submitLabel="Refresh"
        loading={refreshingStats}
        onChange={setRefreshPromptDays}
        onCancel={() => !refreshingStats && setRefreshPromptOpen(false)}
        onSubmit={async () => {
          await refreshCrewStats();
          setRefreshPromptOpen(false);
        }}
      />
    </div>
  );
}
